import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Zap, Check, X, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getRoutePath } from '../../../utils/routingUtils';
import { aiImageFeatureForPackage } from '../../../utils/packageAiImagesFeature';
import PackagePlanFooter from '../../billing/PackagePlanFooter';

const StarterView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const userPackage = user?.result?.package || 1; // Default to 1 (free)
  const isCurrentPackage = userPackage === 2; // Starter = 2

  const features = [
    { included: true, text: t('packages.starter.feature1', 'Do 200 adresa mesečno') },
    { included: true, text: t('packages.starter.feature2', 'Osnovni dashboard') },
    { included: true, text: t('packages.starter.feature3', 'Upravljanje poslovima') },
    { included: true, text: t('packages.starter.feature4', 'Upravljanje klijentima') },
    { included: true, text: t('home.landing.pricing.starter.features.archive', 'Arhiva poslova') },
    { included: true, text: t('home.landing.pricing.starter.features.analytics', 'Analitika poslovanja') },
    aiImageFeatureForPackage(t, 'starter'),
    {
      included: false,
      text: t(
        'superAdmin.packages.starterRobaExcluded',
        'Sekcija Roba (magacin i inventar), izuzev Web shopa'
      ),
    },
    { included: false, text: t('packages.featureVoiceInput', 'Glasovni unos podataka') },
    { included: false, text: t('packages.starter.feature5', 'SMS slanje') },
    { included: false, text: t('packages.starter.feature6', 'Web Shop') },
    { included: false, text: t('packages.starter.feature7', 'AI optimizacija') }
  ];

  return (
    <div className="bg-gray-900 min-h-screen p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-green-700">
          <div className="flex items-center gap-4 mb-4">
            <div className="bg-green-500/20 rounded-lg p-4">
              <Zap className="h-8 w-8 text-green-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">{t('packages.starter.title', 'Starter Paket')}</h1>
              <p className="text-gray-400 mt-1">{t('packages.starter.subtitle', 'Više adresa za rast vašeg posla')}</p>
            </div>
          </div>
          
          {isCurrentPackage && (
            <div className="bg-green-900/30 border border-green-700 rounded-lg p-4 mt-4">
              <div className="flex items-center gap-2 text-green-400">
                <Check className="h-5 w-5" />
                <span className="font-medium">{t('packages.currentPackage', 'Trenutni paket')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold text-white mb-4">{t('packages.features', 'Karakteristike')}</h2>
          <div className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className={feature.detail ? 'space-y-1.5' : ''}>
                <div className="flex items-center gap-3">
                  {feature.included ? (
                    <Check className="h-5 w-5 text-green-400 flex-shrink-0" />
                  ) : (
                    <X className="h-5 w-5 text-gray-600 flex-shrink-0" />
                  )}
                  <span className={feature.included ? 'text-white' : 'text-gray-500 line-through'}>
                    {feature.text}
                  </span>
                </div>
                {feature.detail && (
                  <p className="text-sm text-amber-200/90 leading-snug pl-8 border-l-2 border-amber-500/35 ml-1">
                    {feature.detail}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <PackagePlanFooter packageId={2} theme="green" />

        {!isCurrentPackage && (
          <div className="bg-green-900/20 border border-green-700 rounded-xl p-6">
            <h3 className="text-lg font-bold text-white mb-3">{t('packages.upgradeHigherTitle', 'Nadogradi na viši paket')}</h3>
            <p className="text-gray-300 mb-4">
              {t(
                'packages.starterUpgradeHigherHint',
                'Standard i Business donose više adresa, Roba sekciju i Loyalty. Business dodaje SMS, Mejl, Viber, WhatsApp i Web shop.'
              )}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => navigate(getRoutePath('packages/standard'))}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg transition flex items-center justify-center gap-2"
              >
                {t('packages.upgradeToStandard', 'Standard')}
                <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => navigate(getRoutePath('packages/business'))}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg transition flex items-center justify-center gap-2"
              >
                {t('packages.upgradeToBusiness', 'Business')}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StarterView;

